import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import MenuOpenOutlinedIcon from '@mui/icons-material/MenuOpenOutlined';

export const NAV_RAIL_COLLAPSED_WIDTH = 56;
export const NAV_RAIL_EXPANDED_WIDTH = 240;

export interface NavigationRailItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

interface NavigationRailProps {
  items: NavigationRailItem[];
  headerHeight?: number;
  theme: any;
  ariaLabel: string;
  onExpandedChange?: (width: number) => void;
}

const NavigationRail: React.FC<NavigationRailProps> = ({
  items,
  headerHeight = 56,
  theme,
  ariaLabel,
  onExpandedChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const width = expanded ? NAV_RAIL_EXPANDED_WIDTH : NAV_RAIL_COLLAPSED_WIDTH;
  const isDark = theme?.name === 'dark';
  const borderColor = isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.28)';
  const textColor = theme?.toolbar?.text ?? theme?.toolbar?.buttonText ?? '#e5e7eb';
  const mutedText = isDark ? '#94a3b8' : '#64748b';
  const railBackground = isDark
    ? 'linear-gradient(180deg, rgba(21, 27, 35, 0.96), rgba(15, 20, 25, 0.96))'
    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 250, 252, 0.96))';
  const itemBackground = isDark ? 'rgba(17, 24, 39, 0.34)' : 'rgba(248, 250, 252, 0.66)';
  const activeBackground = isDark ? 'rgba(37, 99, 235, 0.18)' : 'rgba(37, 99, 235, 0.10)';

  const toggleExpanded = () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded ? NAV_RAIL_EXPANDED_WIDTH : NAV_RAIL_COLLAPSED_WIDTH);
  };

  const railSx = {
    position: 'fixed',
    top: headerHeight,
    left: 0,
    bottom: 0,
    width,
    zIndex: 900,
    background: railBackground,
    borderRight: `1px solid ${borderColor}`,
    color: textColor,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    px: '6px',
    py: 1,
    gap: '6px',
    transition: 'width .16s ease, background-color .16s ease',
    boxSizing: 'border-box',
    boxShadow: isDark ? '12px 0 32px rgba(0, 0, 0, 0.18)' : '12px 0 32px rgba(15, 23, 42, 0.08)',
    backdropFilter: 'blur(14px)',
  };

  const buttonBaseSx = {
    width: '100%',
    height: 40,
    minHeight: 40,
    border: '1px solid transparent',
    borderRadius: '8px',
    background: 'transparent',
    color: textColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: expanded ? 'flex-start' : 'center',
    gap: '10px',
    px: expanded ? '10px' : 0,
    cursor: 'pointer',
    boxSizing: 'border-box',
    overflow: 'hidden',
    fontSize: 14,
    fontWeight: 700,
    textAlign: 'left',
    transition: 'transform 160ms ease, border-color 160ms ease, background-color 160ms ease, color 160ms ease',
    '& svg': {
      color: 'inherit',
      flexShrink: 0,
    },
    '&:hover': {
      backgroundColor: itemBackground,
      borderColor: '#2563eb',
      color: textColor,
      transform: 'translateY(-1px)',
    },
    '&:focus-visible': {
      outline: '2px solid #2563eb',
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
  };

  const labelSx = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <Box component="nav" aria-label={ariaLabel} sx={railSx}>
      <Box
        component="button"
        type="button"
        aria-label={expanded ? 'Свернуть меню' : 'Развернуть меню'}
        title={expanded ? 'Свернуть меню' : 'Развернуть меню'}
        onClick={toggleExpanded}
        sx={{
          ...buttonBaseSx,
          mb: 0.5,
          color: mutedText,
          '&:hover': {
            backgroundColor: activeBackground,
            borderColor: '#2563eb',
            color: textColor,
            transform: 'translateY(-1px)',
          },
        }}
      >
        {expanded ? <MenuOpenOutlinedIcon fontSize="small" /> : <MenuOutlinedIcon fontSize="small" />}
        {expanded && <Box component="span" sx={labelSx}>Навигация</Box>}
      </Box>

      {items.map((item) => {
        const itemButton = (
          <Box
            component="button"
            type="button"
            aria-label={item.label}
            aria-current={item.active ? 'page' : undefined}
            disabled={item.disabled}
            title={expanded ? item.label : undefined}
            onClick={item.onClick}
            sx={{
              ...buttonBaseSx,
              backgroundColor: item.active ? activeBackground : 'transparent',
              borderColor: item.active ? '#2563eb' : 'transparent',
              color: item.active ? textColor : mutedText,
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              opacity: item.disabled ? 0.5 : 1,
              boxShadow: item.active ? 'inset 3px 0 0 #2563eb' : 'none',
            }}
          >
            {item.icon}
            {expanded && <Box component="span" sx={labelSx}>{item.label}</Box>}
          </Box>
        );

        return expanded ? (
          <React.Fragment key={item.id}>{itemButton}</React.Fragment>
        ) : (
          <Tooltip key={item.id} title={item.label} placement="right">
            {itemButton}
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default NavigationRail;
